import React, { memo, useEffect, useState, useRef } from 'react'
import { useEmailEntry } from './useEmailEntry.hook'
import styles from './styles.module.scss'
import { REDIRECT_PATH } from 'src/helpers/path'
import { postHttp, getHttp } from 'src/api/apiLayer'
import { PATH_API } from 'src/helpers/path'
import { useAppContext } from 'src/helpers/app'
import ProgressBar from '../ProgressBar'

/**
 * EmailEntryPage Component
 * Collects user's email and validates it before proceeding to the next step
 * @returns {JSX.Element} The EmailEntryPage component
 */
const EmailEntryPage: React.FC = () => {
  const [sessionReady, setSessionReady] = useState(false)
  //Add logic to automatically focus the input field
  const emailInputRef = useRef<HTMLInputElement>(null)
  const { storeURLParameters } = useAppContext()
  const {
    email,
    isValidFormat,
    errorMsg,
    successMsg,
    loading,
    isEmailValid,
    handleEmailChange,
    handleSubmitEmail,
    showError,
    suggestion,
    handleAcceptSuggestion,
  } = useEmailEntry()

  useEffect(() => {
    const ensureSession = async () => {
      let sessionId = localStorage.getItem('sessionID')
      let validSession = false
      if (sessionId) {
        try {
          await getHttp(PATH_API.getSessionId(sessionId))
          validSession = true
        } catch (err) {
          console.error('Error checking session:', err)
        }
      }
      if (!sessionId || !validSession) {
        try {
          const response = await postHttp(PATH_API.createSessionId(), {
            urlParameters: storeURLParameters ?? undefined,
          })
          sessionId = response?.data?.sessionId || response?.sessionId
          if (sessionId) {
            localStorage.setItem('sessionID', sessionId)
          }
        } catch (err) {
          console.error('Error creating session:', err)
        }
      }
      setSessionReady(true)
    }
    ensureSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Add logic to automatically focus the input field
  useEffect(() => {
    if (sessionReady && emailInputRef.current) {
      emailInputRef.current.focus()
    }
  }, [sessionReady])

  if (!sessionReady) return null

  return (
    <>
      <ProgressBar />
      <div className={styles.emailEntryPage}>
        <div className={styles.titleEmailMobile} id="email-entry-title">
          Let’s get started on your perfect wedding flowers
        </div>
        <div className={styles.emailEntryLeftSection}>
          <div className={styles.textContainer}>
            <div className={styles.textContent}>
              <span className={styles.titleEmail}>Let’s get started on your perfect wedding flowers</span>
              <span className={styles.subtitleEmail}>
                First, we’ll ask a few questions to tailor your proposal — starting with your email.
              </span>
              <span className={styles.loginText}>
                Already a client?{' '}
                <a href={REDIRECT_PATH.LOGIN} className={styles.loginLink} aria-label="Login for existing clients">
                  Login
                </a>
              </span>
            </div>
          </div>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.emailForm}>
            <div
              className={`${styles.floatingLabelWrapper} ${email ? styles.hasValue : ''} ${
                !isValidFormat ? styles.hasError : styles.isValid
              }`}
              role="group"
              aria-labelledby="email-label"
            >
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                className={`${styles.input} ${showError ? styles.error : ''} ${
                  isEmailValid && isValidFormat ? styles.valid : ''
                }`}
                placeholder="Your email"
                aria-invalid={!isValidFormat}
                aria-describedby={!isValidFormat ? 'email-error' : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSubmitEmail()
                  }
                }}
                //Add logic to automatically focus the input field
                ref={emailInputRef}
              />
              {loading ? (
                <div className={styles.errorMessage} style={{ color: '#888' }} role="status">
                  Checking email...
                </div>
              ) : suggestion ? (
                <div className={styles.suggestionMessage} role="alert">
                  <span>Did you mean </span>
                  <button
                    className={styles.suggestionLink}
                    onClick={handleAcceptSuggestion}
                    type="button"
                  >
                    {suggestion}
                  </button>
                  <span>?</span>
                </div>
              ) : errorMsg ? (
                <div id="email-error" className={styles.errorMessage} role="alert">
                  <span>⚠︎</span>
                  {errorMsg}
                </div>
              ) : successMsg ? (
                <div className={styles.successMessage} role="status">
                  {successMsg}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <span className={styles.loginTextMobile}>
          Already a client?
          <br />
          <a href={REDIRECT_PATH.LOGIN} className={styles.loginLink} aria-label="Login for existing clients">
            Login
          </a>
        </span>
        <button
          className={`${styles.nextEmailButtonMobile} ${isEmailValid ? styles.validEmail : ''}`}
          onClick={handleSubmitEmail}
          disabled={!email || loading}
        >
          {loading ? 'Checking...' : 'Next'}
        </button>
      </div>

      <div className={styles.continueDesktopContainer}>
        <div className={styles.continueDesktopWrapper}>
          <button
            className={`${styles.continueButton} ${isEmailValid ? styles.validEmail : ''}`}
            onClick={handleSubmitEmail}
            disabled={!email || loading}
          >
            {loading ? 'Checking...' : 'Next'}
          </button>
        </div>
      </div>
    </>
  )
}

EmailEntryPage.displayName = 'EmailEntryPage'

export default memo(EmailEntryPage)
